import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import {
  useAddVendorMutation,
  useUpdateVendorMutation,
} from "src/api/operation-cost/vendors";

interface IProps {
  open: boolean;
  handleClose: () => void;
  row?: any;
  refetch: Function;
  editNameAndBalance?: boolean;
}

const schema = yup.object().shape({
  name: yup.string().required("required"),
  bank_account: yup.string(),
  delivery_days: yup.string(),
  email: yup.string().email("invalidEmail"),
  start_balance: yup.string(),
  tax_number: yup.string(),
  phone: yup.string(),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const AddEditVendorPopup = (props: IProps) => {
  const {
    open,
    handleClose,
    row,
    refetch = () => {},
    editNameAndBalance,
  } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !row ? "add" : "edit";
  const { t } = useTranslation("operationCost");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");

  // APIS
  const { mutate, status, error, isPending } = useAddVendorMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateVendorMutation();

  const { control, handleSubmit, formState, reset, watch } =
    useForm<FormInputs>({
      mode: "onBlur",
      resolver: yupResolver(schema),
    });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const dataToSend = {
      ...data,
      ...(!!data.delivery_days && {
        delivery_days: Number(data.delivery_days),
      }),
      ...(!!data.start_balance && {
        start_balance: Number(data.start_balance),
      }),
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      const dataToUpdate = editNameAndBalance
        ? {
            name: data.name,
            ...(!!data.start_balance && {
              start_balance: Number(data.start_balance),
            }),
          }
        : dataToSend;
      mutateUpdate({
        id: row.id,
        ...dataToUpdate,
      });
    }
  };

  useEffect(() => {
    if (!!row) {
      reset({
        name: row.name || "",
        bank_account: row.bank_account || "",
        tax_number: row.tax_number || "",
        phone: row.phone || "",
        email: row.email || "",
        delivery_days: row.delivery_days || "",
        start_balance: row.start_balance || "",
      });
    }
  }, [row]);

  //Add OC vendor success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update OC vendor success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {mode === "add" ? t("addNewVendor") : t("editVendor")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={purchasingT("name")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                />
              )}
            />

            {!editNameAndBalance ? (
              <>
                {/* account_bank */}
                <Controller
                  name="bank_account"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="string"
                      fullWidth
                      label={purchasingT("accountBank")}
                      size="small"
                      {...field}
                      error={!!formState.errors.bank_account}
                      helperText={formState.errors.bank_account?.message}
                    />
                  )}
                />
                {/* tax number */}
                <Controller
                  name="tax_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="string"
                      fullWidth
                      label={t("vendorTaxNumber")}
                      size="small"
                      {...field}
                      error={!!formState.errors.tax_number}
                      helperText={formState.errors.tax_number?.message}
                    />
                  )}
                />
                {/* Contact number */}
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label={purchasingT("contactNumber")}
                      size="small"
                      {...field}
                      error={!!formState.errors.phone}
                      helperText={generalT(
                        formState.errors.phone?.message ?? "",
                      )}
                    />
                  )}
                />
                {/*  Email */}
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="string"
                      fullWidth
                      label={generalT("email")}
                      size="small"
                      {...field}
                      error={!!formState.errors.email}
                      helperText={generalT(
                        formState.errors.email?.message ?? "",
                      )}
                    />
                  )}
                />
                {/* days delivery */}
                <Controller
                  name="delivery_days"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="string"
                      fullWidth
                      label={purchasingT("expectedDaysDeliver")}
                      size="small"
                      {...field}
                      error={!!formState.errors.delivery_days}
                      helperText={formState.errors.delivery_days?.message}
                    />
                  )}
                />
              </>
            ) : null}

            <Controller
              name="start_balance"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  label={generalT("startBalance")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.start_balance}
                  helperText={formState.errors.start_balance?.message}
                />
              )}
            />
          </Stack>
          <Stack mt={3} spacing={2} direction={"row"}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              loading={isPending || isPendingUpdate}
              type="submit"
              variant="contained"
              fullWidth
            >
              {generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditVendorPopup;
